import React, { useState, useMemo } from 'react';
import { Card } from './Card';
import Slideover from './Slideover';
import StatusPill from './StatusPill';
import { formatDate } from '../../utils/formatDate';
import { normaliseSalespeople } from '../../utils/normaliseSalespeople';
// IDI-152: normalizeMarginValue imported directly per operator ruling this
// turn. Two local aliases exist in the codebase (ChangeOrderBuilderPage.js:41,
// buildViewQuote.js:32); do NOT add a third — F5 catches it.
import { normalizeMarginValue } from '../../quote-builder/quotePrefill';
// IDI-153: resolve officeId to its office name. useSettings' getEnumLabel is the
// documented helper for rendering an enum value on a customer-visible surface --
// never the raw id (see useSettings.js docstring).
import { useSettingsContext } from '../../hooks/useSettings';
import { SalespeopleChips } from './SalespeopleChips';
import {
  ChevronRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  DocumentDuplicateIcon,
  MapPinIcon,
  UsersIcon,
  UserGroupIcon,
  TagIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  ReceiptPercentIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';

/**
 * GoldenCustomerCard — canonical customer-info card component (APP-02 spec).
 *
 * Renders on: Customer Detail (primary), Quote Builder, Job Detail, Work Order,
 * Change Order — wherever a saved customer is displayed as the page identity.
 *
 * Wireframe source-of-truth:
 *   Portica Clients/IDI/BIDIT/Iterations/Iteration_18/Customer_Card_Options/
 *   APP-02-customer-info-card-COMPONENT.html
 *
 * Design tokens (mapped from wire ad-hoc hex):
 *   --ink       → var(--color-text-default)
 *   --line      → var(--color-border)
 *   --line2     → var(--color-border-strong)
 *   --muted     → var(--color-text-muted)
 *   --ok        → var(--color-success)  (Active status pill)
 *   --neutral-* → count/pill/hover backgrounds
 *
 * Props:
 *   customer  — full customer object (name, company, type, status, contacts[],
 *               addresses[], salespersons[], payment_terms, payment_type, etc.)
 *   surface   — 'customer' | 'job' | 'workorder' | 'quote' | 'changeorder'
 *   variant   — 'chevron' (default) | 'preview'
 *                 chevron: plural labels + count badges + chevrons + click-to-open
 *                          quick-view sheets. Used by Customer Detail (existing),
 *                          Job Detail (future), WO Detail (future).
 *                 preview: singular labels, no chevron, no count badge,
 *                          non-interactive salespeople +N. Used by Quote Builder
 *                          (this PR), Change Order (future). Empty state renders
 *                          `Contact · None on file` with no interactive Add link.
 *                 Q03b (PR-3) regression lock: default MUST remain 'chevron' so
 *                 CustomerDetail.js (which passes no variant prop) is byte-identical.
 *   onEdit    — click handler for the Edit button
 *   actions   — optional extra actions (rendered next to Edit)
 *   className — optional wrapper className
 *   jobRows   — IDI-478: optional [{ label, value, title?, icon? }] rendered in
 *               the KV grid on surface === 'job' ONLY (job type, quote,
 *               contract total). Ignored on every other surface.
 */
export default function GoldenCustomerCard({
  customer,
  surface = 'customer',
  variant = 'chevron',
  onEdit,
  actions,
  className,
  jobRows,
  // IDI-582 S4/D4: the customer page passes true for a field tech, so the
  // customer's margin is never shown to a crew.
  hideMargin = false,
}) {
  const isPreview = variant === 'preview';
  const [openSheet, setOpenSheet] = useState(null); // 'contacts' | 'addresses' | 'salespeople' | null

  const contacts = useMemo(() => customer?.contacts || [], [customer?.contacts]);
  // §3 (293_): fall back to singular `address`/`serviceAddress` when `addresses`
  // is empty. N-14 canonical: `address` (map street/city/state/zip);
  // `serviceAddress` uses (address1/city/state/postal) — map on read.
  const addresses = useMemo(() => {
    const arr = customer?.addresses || [];
    if (arr.length > 0) return arr;
    const a = customer?.address;
    if (a && (a.street || a.line1 || a.city || a.zip)) {
      // §1 fix (294_): map wire `street` → card `line1`. Both branches emit line1.
      return [{
        line1: a.line1 || a.street,
        line2: a.line2,
        city: a.city,
        state: a.state,
        zip: a.zip,
        is_primary: true,
      }];
    }
    const s = customer?.serviceAddress;
    if (s && (s.address1 || s.city || s.postal)) {
      return [{
        line1: s.address1,
        city: s.city,
        state: s.state,
        zip: s.postal,
        is_primary: true,
      }];
    }
    return [];
  }, [customer?.addresses, customer?.address, customer?.serviceAddress]);
  const salespeople = normaliseSalespeople(customer);

  const primaryContact = useMemo(
    () => contacts.find(c => c.is_primary || c.isPrimary) || contacts[0],
    [contacts]
  );
  const primaryAddress = useMemo(
    () => addresses.find(a => a.is_primary || a.isPrimary) || addresses[0],
    [addresses]
  );

  // Display name — company preferred if use_company_as_display flag is on,
  // else the customer's name field.
  const displayName = customer?.use_company_as_display && customer?.company
    ? customer.company
    : (customer?.name || 'Untitled Customer');

  const status = (customer?.status || 'active').toLowerCase();
  // IDI-153: shared settings cache; getEnumLabel resolves officeId -> office name.
  const { getEnumLabel } = useSettingsContext();
  // IDI-495 / IDI-399 item 3: the dealer's own type name ("Builder"), not a
  // stored id like "2". getEnumLabel falls back to a prettified value.
  const rawType = customer?.customer_type || customer?.type || null;
  const typeLabel = rawType ? getEnumLabel('customer_types', rawType) : null;

  // Per-surface attribute list (per wire §2 per-surface variation).
  // customer  → payment terms · pays by · customer name (parenthetical if company-as-display)
  // job/quote/changeorder → payment terms · pays by
  // workorder → none (payments omitted)
  const showPaymentRows = surface !== 'workorder';
  // R2: Customer type row — shown on 'customer' surface when a type is set.
  // Deliberately excluded from job/quote/wo/co (those surfaces already show
  // their own type/status pill).
  const showCustomerTypeRow = surface === 'customer' && !!typeLabel;

  // ─── IDI-152 (absorbs IDI-155 + IDI-151, 2026-09-07) ─────────────────
  //
  // Two new rows: Default Gross Margin % and Date Created.
  //
  // GATE — surface === 'customer' ONLY. Default margin is commercially
  // sensitive: this card renders on the workorder surface too, and field
  // techs see the WO page. An ungated margin row puts the customer's
  // margin in front of a crew on site. The gate here is the highest-
  // consequence line in this component today.
  //
  // Wire fields:
  //   · customer.default_margin  — number or string, stored in the
  //     shape AddCustomerDrawer.js:385 writes. May be either "30"
  //     (percent-style string) or 0.3 (decimal). normalizeMarginValue
  //     accepts both and returns a decimal 0..1 (or null if unset/invalid).
  //   · customer.createdAt / customer.created_at — ISO string, camelCase
  //     canonical, snake_case legacy. Reader mirrors the pattern from
  //     JobsPage.js:611 (IDI-139 §1).
  //
  // Rendering:
  //   · Margin: Math.round(decimal * 100) + '%'. null → em-dash.
  //   · Date:   formatDate(customer.createdAt || customer.created_at).
  //             Never toLocaleDateString (utils/formatDate.js:9 rule).
  const marginDecimal = normalizeMarginValue(customer?.default_margin);
  const marginDisplay =
    marginDecimal == null ? '\u2014' : `${Math.round(marginDecimal * 100)}%`;
  const dateCreatedDisplay =
    formatDate(customer?.createdAt || customer?.created_at) || '—';
  const showDefaultMarginRow = surface === 'customer' && !hideMargin;
  const showDateCreatedRow = surface === 'customer';

  // IDI-153: Office on the customer record.
  //
  // Multi-office dealers need to know which office owns a customer. The id is
  // on the record (measured: 38 of 54 tenant-1 customers carry officeId) and
  // resolves against the offices list -- but nothing rendered it, so the field
  // was invisible everywhere in the app.
  //
  // Resolve through getEnumLabel, never the raw id: that is the documented
  // contract for enum values on a customer-visible surface (useSettings.js
  // docstring). It falls back through the seed/tenant-default chain and
  // prettifies an unmatched value rather than printing a bare number.
  //
  // Row is hidden entirely when the customer has no office, so single-office
  // dealers do not see a permanent em-dash for a concept they never use.
  const officeId = customer?.officeId ?? customer?.office_id ?? null;
  const officeDisplay =
    officeId == null || officeId === ''
      ? null
      : getEnumLabel('offices', String(officeId));
  const showOfficeRow = surface === 'customer' && !!officeDisplay;
  // IDI-478: job-level rows (job type, quote, contract total). Gated to the
  // job surface so the card on every other surface is unchanged.
  const visibleJobRows = surface === 'job' && Array.isArray(jobRows) ? jobRows.filter(Boolean) : [];
  const showJobRows = visibleJobRows.length > 0;
  const showCustomerNameRow = surface === 'customer'
    && customer?.use_company_as_display
    && customer?.name
    && customer.company
    && customer.name !== customer.company;

  return (
    <>
      <Card className={className} data-portica-role="golden-customer-card">
        <div className="grid gap-0" style={{ gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)' }}>
          {/* ─── LEFT column ─────────────────────────────────────────── */}
          <div className="pt-5 pb-4 pl-8 pr-4 flex flex-col h-full">
            {/* Name + status pill */}
            <div className="flex items-center gap-2 flex-wrap">
              <h2
                className="m-0"
                style={{
                  fontFamily: 'var(--font-display, Rajdhani, Arial)',
                  fontSize: 28,
                  lineHeight: 1.15,
                  fontWeight: 700,
                  color: 'var(--color-text-default)',
                }}
              >
                {displayName}
              </h2>
              <StatusPill status={status} title="Customer status" />
            </div>

            {/* Meta line: record name only (type moved to KV grid in R2). */}
            {customer?.use_company_as_display && customer?.name && customer.name !== customer.company && (
              <div
                className="mt-1 text-xs"
                style={{ color: 'var(--color-text-muted)' }}
              >
                record name: {customer.name}
              </div>
            )}

            {/* KV attribute list. Iter18 Golden Card R2:
                • Customer type joins Payment terms + Pays by as a KV row (was in meta line).
                • Each label gets a leading icon for scan-ability.
                • Label column widened to 148px to fit icon + text.
            */}
            {/* IDI-152 (2026-09-07): showDefaultMarginRow + showDateCreatedRow
                added to the outer KV-grid gate. Both are surface==='customer'
                gated too (per-row gate is the primary protection). Adding
                to the outer condition means: on 'customer' surface, if all
                other rows happen to be false, the grid still renders for the
                two IDI-152 rows. On non-customer surfaces, all four falsify
                and the outer gate hides the grid. Both gates agree. */}
            {(showJobRows || showPaymentRows || showCustomerTypeRow || showCustomerNameRow || showDefaultMarginRow || showDateCreatedRow || showOfficeRow) && (
              <div
                className="mt-3 grid gap-y-2 gap-x-3 text-sm items-center"
                style={{ gridTemplateColumns: '148px 1fr' }}
              >
                {/* IDI-478: job-only rows — surface === 'job' only. */}
                {showJobRows && visibleJobRows.map((row) => (
                  <React.Fragment key={row.label}>
                    <KVLabel icon={row.icon || TagIcon}>{row.label}</KVLabel>
                    <div title={row.title || undefined} data-testid={`job-row-${String(row.label).toLowerCase().replace(/\s+/g, '-')}`}>
                      {row.value}
                    </div>
                  </React.Fragment>
                ))}
                {showCustomerTypeRow && (
                  <>
                    <KVLabel icon={TagIcon}>Customer type</KVLabel>
                    <div>{typeLabel}</div>
                  </>
                )}
                {showPaymentRows && (
                  <>
                    <KVLabel icon={CalendarDaysIcon}>Payment terms</KVLabel>
                    {/* IDI-495: resolve through the dealer's list (a stored row
                      * id, legacy id or slug all read as the row's label). */}
                    <div>{customer?.payment_terms
                      ? getEnumLabel('payment_terms', customer.payment_terms)
                      : <span className="italic" style={{ color: 'var(--color-text-muted)' }}>Not set</span>}</div>
                    <KVLabel icon={CreditCardIcon}>Pays by</KVLabel>
                    <div>{customer?.payment_type
                      ? getEnumLabel('payment_types', customer.payment_type)
                      : <span className="italic" style={{ color: 'var(--color-text-muted)' }}>Not set</span>}</div>
                  </>
                )}
                {showCustomerNameRow && (
                  <>
                    <KVLabel icon={TagIcon}>Customer name</KVLabel>
                    <div>
                      {customer.name}
                      <span className="ml-1" style={{ color: 'var(--color-text-muted)' }}>
                        (company shown as display name)
                      </span>
                    </div>
                  </>
                )}
                {/* IDI-153 (2026-09-28): Office — which office owns this customer.
                    Multi-office dealers need it; the id was on the record and
                    resolvable but nothing rendered it. surface === 'customer'
                    only, matching the gates above. Resolved via getEnumLabel
                    (never the raw id) per the useSettings contract. The row is
                    omitted entirely when the customer has no office, so
                    single-office dealers do not see a permanent em-dash. */}
                {showOfficeRow && (
                  <>
                    <KVLabel icon={BuildingOffice2Icon}>Office</KVLabel>
                    <div>{officeDisplay}</div>
                  </>
                )}
                {/* IDI-152 (2026-09-07): Default Gross Margin % — commercially
                    sensitive, gated to surface === 'customer' so it NEVER
                    appears on wo/job/quote/co surfaces. Field techs work on
                    the WO surface; an ungated margin row would leak the
                    customer's margin to a crew on site. Reader routed
                    through normalizeMarginValue (imported, not aliased).
                    Returns decimal 0..1 or null; null → em-dash, never 0%. */}
                {showDefaultMarginRow && (
                  <>
                    <KVLabel icon={ReceiptPercentIcon}>Default gross margin</KVLabel>
                    <div>{marginDisplay}</div>
                  </>
                )}
                {/* IDI-152 (2026-09-07): Date Created — surface === 'customer'
                    only (matches Default Margin gate). Reader mirrors the
                    IDI-139 §1 pattern from JobsPage.js:611: camelCase canonical
                    with snake_case fallback, routed through the shared
                    formatDate utility (utils/formatDate.js). Never
                    toLocaleDateString (F6 falsifier). */}
                {showDateCreatedRow && (
                  <>
                    <KVLabel icon={CalendarDaysIcon}>Date created</KVLabel>
                    <div>{dateCreatedDisplay}</div>
                  </>
                )}
              </div>
            )}

            {/* C3: developer note removed. Payment terms and Pays by are
                intentionally not shown on the WO surface (they live on the
                quote/job level). The note was never meant to ship as UI copy. */}

            {/* Action row (Iter18 Golden Card R2: Edit CTA removed per operator —
                edit is available via the header kebab or per-segment inline
                'Add' link when a segment is empty). */}
            {actions && (
              <div className="mt-auto pt-3.5 flex gap-2 flex-wrap items-center">
                {actions}
              </div>
            )}
          </div>

          {/* ─── RIGHT column: segments ─────────────────────────────── */}
          <div
            style={{ borderLeft: '1px solid var(--color-border)' }}
          >
            <Segment
              variant={variant}
              label={isPreview ? 'Contact' : 'Contacts'}
              icon={UsersIcon}
              count={contacts.length}
              hideCountBadge={isPreview}
              preview={contactPreview(primaryContact)}
              onClick={!isPreview && contacts.length > 0 ? () => setOpenSheet('contacts') : undefined}
              onAdd={!isPreview && contacts.length === 0 ? onEdit : undefined}
            />
            <Segment
              variant={variant}
              label={isPreview ? 'Address' : 'Addresses'}
              icon={MapPinIcon}
              count={addresses.length}
              hideCountBadge={isPreview}
              preview={addressPreview(primaryAddress, addresses)}
              onClick={!isPreview && addresses.length > 0 ? () => setOpenSheet('addresses') : undefined}
              onAdd={!isPreview && addresses.length === 0 ? onEdit : undefined}
            />
            <Segment
              variant={variant}
              label="Salespeople"
              icon={UserGroupIcon}
              count={salespeople.length}
              hideCountBadge
              preview={<SalespeopleChips salespeople={salespeople} interactive={!isPreview} style={{ marginTop: '0.3rem' }} />}
              onClick={!isPreview && salespeople.length > 0 ? () => setOpenSheet('salespeople') : undefined}
              onAdd={!isPreview && salespeople.length === 0 ? onEdit : undefined}
              isLast
            />
          </div>
        </div>
      </Card>

      {/* ─── Quick-view sheets ─────────────────────────────────────── */}
      <ContactsSheet
        open={openSheet === 'contacts'}
        onClose={() => setOpenSheet(null)}
        contacts={contacts}
      />
      <AddressesSheet
        open={openSheet === 'addresses'}
        onClose={() => setOpenSheet(null)}
        addresses={addresses}
      />
      <SalespeopleSheet
        open={openSheet === 'salespeople'}
        onClose={() => setOpenSheet(null)}
        salespeople={salespeople}
      />
    </>
  );
}

// ─── Segment row ────────────────────────────────────────────────────

// KV label helper: icon + text, muted color. Used by the attribute-list rows
// on the left column (Customer type / Payment terms / Pays by / Customer name).
function KVLabel({ icon: Icon, children }) {
  return (
    <div
      className="inline-flex items-center gap-1.5"
      style={{ color: 'var(--color-text-muted)' }}
    >
      {Icon && <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
      <span>{children}</span>
    </div>
  );
}

// Sheet title helper: renders {label} + a pill-shaped count badge matching the
// same styling as the segment count badges on the card (light neutral bg,
// bold number, rounded-full).
function SheetTitle({ label, count }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span>{label}</span>
      {typeof count === 'number' && (
        <span
          className="rounded-full"
          style={{
            background: 'var(--neutral-100)',
            color: 'var(--color-text-default)',
            fontSize: 12,
            padding: '1px 9px',
            fontWeight: 700,
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          {count}
        </span>
      )}
    </span>
  );
}

function Segment({ variant = 'chevron', label, icon: Icon, count, hideCountBadge, preview, onClick, onAdd, isLast }) {
  const isLink = !!onClick;
  // Q03 (PR-3): preview variant hides chevron unconditionally. chevron variant
  // keeps the pre-PR-3 rule: show when linked and count is undefined or >= 2.
  const showChevron = variant === 'chevron' && isLink && (count === undefined || count >= 2);

  return (
    <div
      role={isLink ? 'button' : undefined}
      tabIndex={isLink ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (isLink && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className={[
        'flex justify-between items-center gap-3 px-4 py-2.5',
        !isLast ? 'border-b' : '',
        isLink ? 'cursor-pointer hover:bg-[var(--neutral-50)]' : '',
      ].join(' ')}
      style={{ borderColor: 'var(--color-border)' }}
    >
      <div className="min-w-0 flex-1">
        <div
          className="flex items-center gap-1.5 uppercase tracking-wide"
          style={{
            fontSize: 10.5,
            color: 'var(--color-text-muted)',
            letterSpacing: '0.06em',
            fontWeight: 500,
          }}
        >
          {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
          <span>{label}</span>
          {!hideCountBadge && typeof count === 'number' && (
            <span
              className="rounded-full"
              style={{
                background: 'var(--neutral-100)',
                color: 'var(--color-text-default)',
                fontSize: 10.5,
                padding: '1px 7px',
                fontWeight: 700,
              }}
            >
              {count}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-sm" style={{ lineHeight: 1.55 }}>
          {count === 0 ? (
            // Preview variant: no interactive Add — parent card provides the Edit CTA.
            // Chevron variant with onAdd: interactive Add link (pre-PR-3 behavior).
            onAdd ? (
              <span style={{ color: 'var(--color-text-muted)' }}>
                None on file · <button onClick={(e) => { e.stopPropagation(); onAdd(); }} className="underline hover:no-underline" style={{ color: 'var(--color-text-default)' }}>Add</button>
              </span>
            ) : (
              <span style={{ color: 'var(--color-text-muted)' }}>None on file</span>
            )
          ) : preview}
        </div>
      </div>
      {showChevron && (
        <ChevronRightIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
      )}
    </div>
  );
}

// ─── Preview builders ───────────────────────────────────────────────

function contactPreview(contact) {
  if (!contact) return null;
  const name = contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
  const bits = [];
  if (contact.title || contact.role) bits.push(contact.title || contact.role);
  if (contact.phone) bits.push(contact.phone);
  return (
    <>
      <b>{name || 'Unnamed contact'}</b>
      {bits.length > 0 && (
        <span style={{ color: 'var(--color-text-muted)' }}> · {bits.join(' · ')}</span>
      )}
    </>
  );
}

function addressPreview(primary, all) {
  if (!primary) return null;
  const line = [primary.line1, primary.city, primary.state, primary.zip].filter(Boolean).join(', ');
  const billing = all.find(a => a.type === 'billing' || a.is_billing);
  const differs = billing && billing !== primary;
  return (
    <>
      {line || 'Address on file'}
      {differs && (
        <span style={{ color: 'var(--color-text-muted)' }}> · billing differs</span>
      )}
    </>
  );
}

// SalespeopleChips extracted to `./SalespeopleChips.js` (PT-53b F8, 2026-08-03)
// per SD-6 — consumers measured, shared component. Import at file head.

// ─── Quick-view sheets ──────────────────────────────────────────────

function ContactsSheet({ open, onClose, contacts }) {
  // Primary first (per wire "designated Primary marked and sorted first").
  const sorted = useMemo(() => {
    const primary = contacts.filter(c => c.is_primary || c.isPrimary);
    const rest = contacts.filter(c => !(c.is_primary || c.isPrimary));
    return [...primary, ...rest];
  }, [contacts]);

  return (
    <Slideover open={open} onClose={onClose} title={<SheetTitle label="Contacts" count={contacts.length} />} width="md">
      <div className="space-y-2 pb-4">
        {sorted.map((c, i) => (
          <ContactRow key={c.id || i} contact={c} isPrimary={c.is_primary || c.isPrimary} />
        ))}
      </div>
    </Slideover>
  );
}

function ContactRow({ contact, isPrimary }) {
  const name = contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed';
  return (
    <div
      className="rounded p-3"
      style={{
        border: `${isPrimary ? '2px' : '1px'} solid ${isPrimary ? 'var(--color-text-default)' : 'var(--color-border)'}`,
        background: isPrimary ? 'var(--color-surface)' : 'transparent',
      }}
    >
      <div className="flex justify-between items-start gap-2">
        <div>
          <b>{name}</b>
          {isPrimary && (
            <span
              className="ml-2 rounded-full text-xs"
              style={{
                background: 'var(--neutral-100)',
                color: 'var(--color-text-muted)',
                padding: '2px 10px',
                fontWeight: 700,
              }}
            >
              Primary
            </span>
          )}
          {(contact.title || contact.role) && (
            <div className="mt-0.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {contact.title || contact.role}
            </div>
          )}
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {contact.phone && (
            <a href={`tel:${contact.phone}`} className="p-1 rounded hover:bg-[var(--neutral-100)]" aria-label="Call">
              <PhoneIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
            </a>
          )}
          {contact.email && (
            <a href={`mailto:${contact.email}`} className="p-1 rounded hover:bg-[var(--neutral-100)]" aria-label="Email">
              <EnvelopeIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
            </a>
          )}
          <button
            onClick={() => navigator.clipboard?.writeText([name, contact.phone, contact.email].filter(Boolean).join(' · '))}
            className="p-1 rounded hover:bg-[var(--neutral-100)]"
            aria-label="Copy"
          >
            <DocumentDuplicateIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>
      </div>
      <div className="mt-2 space-y-0.5 text-sm">
        <div style={{ color: contact.phone ? 'var(--color-text-default)' : 'var(--color-text-muted)' }}>
          {contact.phone || 'No phone on file'}
        </div>
        <div style={{ color: contact.email ? 'var(--color-text-default)' : 'var(--color-text-muted)' }}>
          {contact.email || 'No email on file'}
        </div>
      </div>
    </div>
  );
}

function AddressesSheet({ open, onClose, addresses }) {
  const service = addresses.filter(a => !(a.type === 'billing' || a.is_billing));
  const billing = addresses.filter(a => a.type === 'billing' || a.is_billing);
  return (
    <Slideover open={open} onClose={onClose} title={<SheetTitle label="Addresses" count={service.length} />} width="md">
      <div className="space-y-2 pb-4">
        {service.map((a, i) => (
          <AddressRow key={a.id || i} address={a} isPrimary={a.is_primary || a.isPrimary} />
        ))}
        {billing.length > 0 && (
          <>
            <div
              className="mt-4 uppercase tracking-wider text-xs font-semibold flex items-center gap-1.5"
              style={{ color: 'var(--color-text-muted)', letterSpacing: '0.06em' }}
            >
              Billing address
            </div>
            {billing.map((a, i) => (
              <AddressRow key={`b-${a.id || i}`} address={a} isBilling />
            ))}
            <div className="mt-1 text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
              Billing address is not counted in the segment total.
            </div>
          </>
        )}
      </div>
    </Slideover>
  );
}

function AddressRow({ address, isPrimary, isBilling }) {
  const mapUrl = `https://maps.google.com/?q=${encodeURIComponent(
    [address.line1, address.city, address.state, address.zip].filter(Boolean).join(', ')
  )}`;
  const label = isPrimary ? 'Default service' : (isBilling ? 'Billing' : null);
  return (
    <div
      className="rounded p-3"
      style={{
        border: `${isPrimary ? '2px' : '1px'} solid ${isPrimary ? 'var(--color-text-default)' : 'var(--color-border)'}`,
        background: isPrimary ? 'var(--color-surface)' : 'transparent',
      }}
    >
      <div className="flex justify-between items-start gap-2">
        <div>
          <b>{address.line1 || 'Address'}</b>
          {label && (
            <span
              className="ml-2 rounded-full text-xs"
              style={{
                background: 'var(--neutral-100)',
                color: 'var(--color-text-muted)',
                padding: '2px 10px',
                fontWeight: 700,
              }}
            >
              {label}
            </span>
          )}
          <div className="mt-0.5 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {[address.city, address.state, address.zip].filter(Boolean).join(', ')}
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <a href={mapUrl} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-[var(--neutral-100)]" aria-label="Open in maps">
            <MapPinIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
          </a>
          <button
            onClick={() => navigator.clipboard?.writeText([address.line1, address.city, address.state, address.zip].filter(Boolean).join(', '))}
            className="p-1 rounded hover:bg-[var(--neutral-100)]"
            aria-label="Copy"
          >
            <DocumentDuplicateIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SalespeopleSheet({ open, onClose, salespeople }) {
  return (
    <Slideover open={open} onClose={onClose} title={<SheetTitle label="Salespeople" count={salespeople.length} />} width="md">
      <div className="space-y-2 pb-4">
        {salespeople.map((sp, i) => (
          <div
            key={sp.id || i}
            className="rounded p-3"
            style={{ border: '1px solid var(--color-border)' }}
          >
            <div className="flex justify-between items-start gap-2">
              <div className="flex items-center gap-2">
                <span
                  className="rounded-full inline-flex items-center justify-center"
                  style={{
                    width: 32,
                    height: 32,
                    background: 'var(--neutral-200)',
                    color: 'var(--color-text-default)',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {initials(sp.name)}
                </span>
                <div>
                  <b>{sp.name}</b>
                  <div className="mt-0.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    Salesperson
                  </div>
                </div>
              </div>
              {sp.email && (
                <a href={`mailto:${sp.email}`} className="p-1 rounded hover:bg-[var(--neutral-100)]" aria-label="Email">
                  <EnvelopeIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
                </a>
              )}
            </div>
          </div>
        ))}
        <div className="mt-2 text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
          No designated primary — salespeople are peers (CU-1). Order = as assigned.
        </div>
      </div>
    </Slideover>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function initials(name) {
  if (!name) return '·';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase())
    .join('') || '·';
}

// Accept both singular and plural, snake and camel — customers may store salespeople
// as `salespersons`, `salespeople`, `sales_person`, or as an array of ids that need
// normaliseSalespeople — extracted to src/utils/normaliseSalespeople.js (DEC-48/DEC-50).
// Two consumers: this file + quotePrefill.js. See COMPONENT_MAP.md.
